/**
 * Phase 14b — canonical pricing copy for the Lite → Home upgrade flow.
 *
 * Source of truth: `MVP.md` §4.9 and `BizDocs/Marketing.md` §4.2. The
 * MVP wording is verbatim:
 *
 *   "Unlock everything for $24.99 (one-time). Year 1 AI included."
 *   with a secondary "Or $2.99/month" line.
 *
 * Every upgrade-decision affordance in the app — `TierCapBanner`
 * (Phase 13b), the Reports-hub `Upgrade to unlock` card (Phase 13g),
 * the standalone `/pricing` route (this slice) — imports from here.
 * Co-locating the strings with the rest of the `@panelgenie/contracts`
 * surface mirrors the Phase 14a SLA-copy pattern (`ACCOUNT_EXPORT_SLA_COPY`
 * / `ACCOUNT_DELETE_SLA_COPY`): one constant the surface and any future
 * server-side renderer (email templates, marketing-landing handoff)
 * read from.
 *
 * Out of scope at 14b (deferred per the slice spec):
 *
 * - **Pro tier price points.** Pro lands post-MVP; the constants here
 *   are Home-tier only.
 * - **Discount-code wiring.** Stripe handles promotion codes server-
 *   side once promo codes are issued; the display copy stays fixed at
 *   MVP.
 * - **A/B-test infrastructure** for price string variants. Marketing
 *   §10.7 calls out price-elasticity tests as a post-launch sweep.
 * - **Currency localization.** US-only at launch per Marketing §7.4;
 *   the strings are USD-only and the `$` prefix is hard-coded.
 *
 * Why these are `as const` string literals (not Zod schemas):
 *
 * The pricing copy is *display* data, not a wire-shape contract.
 * Nothing on either side of the tRPC boundary parses these strings —
 * the surface imports the literal and renders it. Modeling them as
 * Zod schemas would invert the dependency (the schema validates the
 * literal, which is the canonical value) without buying anything.
 * The `as const` shape gives downstream consumers exact-literal types
 * for compile-time pinning if a future surface wants to discriminate
 * on the canonical value.
 */

/**
 * Home tier one-time unlock price, USD. Display-only string; the
 * authoritative Stripe price id lookup is server-side (Phase 13h's
 * `StripeCheckoutService.createCheckoutSession`).
 */
export const HOME_TIER_ONE_TIME_PRICE = "$24.99" as const;

/**
 * Home tier monthly subscription alternative, USD. Display-only.
 * Per Marketing §5.2 the one-time price is the default; the monthly
 * line is the secondary affordance.
 */
export const HOME_TIER_MONTHLY_PRICE = "$2.99" as const;

/**
 * Primary upgrade-prompt copy per `MVP.md` §4.9. Verbatim. The
 * "Year 1 AI included" half is non-negotiable — it is the user-facing
 * answer to the "what about ongoing AI cost" objection Marketing §5.3
 * documents.
 */
export const HOME_TIER_PRIMARY_PRICING_COPY =
  "Unlock everything for $24.99 (one-time). Year 1 AI included." as const;

/**
 * Secondary upgrade-prompt copy per `MVP.md` §4.9. Verbatim. The
 * subscription alternative is *offered* but not the default per
 * Marketing §5.2; the wording reflects that ("Or" prefix, not "Pay
 * monthly instead").
 */
export const HOME_TIER_SECONDARY_PRICING_COPY = "Or $2.99/month" as const;

/**
 * Composite record consumers can pass through props without
 * importing every constant individually. The field names mirror the
 * MVP §4.9 prompt structure (primary line + secondary line).
 *
 * The `pricingForHomeTier()` factory returns the canonical record;
 * tests use it to assert wire-shape equality without restating the
 * constants. Pure; takes no arguments.
 */
export type HomeTierPricing = {
  readonly oneTimePrice: typeof HOME_TIER_ONE_TIME_PRICE;
  readonly monthlyPrice: typeof HOME_TIER_MONTHLY_PRICE;
  readonly primaryCopy: typeof HOME_TIER_PRIMARY_PRICING_COPY;
  readonly secondaryCopy: typeof HOME_TIER_SECONDARY_PRICING_COPY;
};

export const pricingForHomeTier = (): HomeTierPricing => ({
  oneTimePrice: HOME_TIER_ONE_TIME_PRICE,
  monthlyPrice: HOME_TIER_MONTHLY_PRICE,
  primaryCopy: HOME_TIER_PRIMARY_PRICING_COPY,
  secondaryCopy: HOME_TIER_SECONDARY_PRICING_COPY,
});
